import React, { useCallback, useEffect, useRef, useState } from 'react';
import { taches, messageErreur } from '../services/api';
import { Chargement, Erreur, IconeFichier, IconeImage, IconeTelecharger, IconeTrombone, Modale } from './communs';
import { horodatage, ilYA, nomComplet, taille } from '../utils/format';

const TAILLE_MAX = 10 * 1024 * 1024;
const TEXTE_MAX = 1024 * 1024; // au-delà, l'aperçu texte est tronqué

// Ce que la modale sait montrer. Le reste se télécharge.
function genre(f) {
  const t = f.type_mime;
  if (t.startsWith('image/') && t !== 'image/tiff') return 'image';
  if (t === 'application/pdf') return 'pdf';
  if (t.startsWith('video/')) return 'video';
  if (t.startsWith('audio/')) return 'audio';
  if (t.startsWith('text/') || ['application/json', 'application/xml'].includes(t)) return 'texte';
  return null;
}

// Enregistre un Blob sous son nom d'origine.
function enregistrer(blob, nom) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nom;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Pièces jointes d'une tâche : liste, ajout (bouton ou glisser-déposer),
 * téléchargement, aperçu en modale. L'ajout est enregistré tout de suite,
 * sans passer par le bouton « Enregistrer » de la fiche.
 */
export default function PiecesJointes({ tacheId, onChange }) {
  const [fichiers, setFichiers] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [envois, setEnvois] = useState([]); // { nom, pc }
  const [survol, setSurvol] = useState(false);
  const [apercu, setApercu] = useState(null); // index dans `fichiers`
  const champ = useRef(null);

  const charger = useCallback(() => taches.fichiers(tacheId)
    .then((f) => { setFichiers(f); return f; })
    .catch((e) => setErreur(messageErreur(e))), [tacheId]);

  useEffect(() => { setFichiers(null); setErreur(null); setApercu(null); charger(); }, [charger]);

  async function joindre(liste) {
    const choisis = [...liste];
    if (!choisis.length) return;
    setErreur(null);
    const refuses = choisis.filter((f) => f.size > TAILLE_MAX || !f.size);
    if (refuses.length) {
      setErreur(`${refuses.map((f) => `« ${f.name} »`).join(', ')} : ${refuses.length > 1 ? 'fichiers vides ou trop volumineux' : 'fichier vide ou trop volumineux'} (10 Mo au plus).`);
    }
    const valides = choisis.filter((f) => !refuses.includes(f));
    setEnvois(valides.map((f) => ({ nom: f.name, pc: 0 })));
    for (let i = 0; i < valides.length; i += 1) {
      try {
        await taches.joindre(tacheId, valides[i], (pc) => setEnvois((l) => l.map((x, j) => (j === i ? { ...x, pc } : x))));
      } catch (e) {
        setErreur(`« ${valides[i].name} » : ${messageErreur(e)}`);
      }
    }
    setEnvois([]);
    if (valides.length) { await charger(); onChange?.(); }
  }

  async function retirer(f) {
    if (!window.confirm(`Retirer le fichier « ${f.nom} » de la tâche ? C’est définitif.`)) return;
    try {
      await taches.retirerFichier(tacheId, f.id);
      setApercu(null);
      await charger();
      onChange?.();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  }

  async function telecharger(f) {
    try { enregistrer(await taches.contenuFichier(tacheId, f), f.nom); } catch (e) { setErreur(`« ${f.nom} » : ${messageErreur(e)}`); }
  }

  const deposer = (e) => {
    e.preventDefault();
    setSurvol(false);
    joindre(e.dataTransfer.files);
  };
  const glisse = (e) => {
    if (![...e.dataTransfer.types].includes('Files')) return;
    e.preventDefault();
    setSurvol(true);
  };

  return (
    <div
      className={`panneau-section pj${survol ? ' survol' : ''}`}
      onDragOver={glisse}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setSurvol(false); }}
      onDrop={deposer}
    >
      <div className="pj-entete">
        <h3 className="carte-titre">Pièces jointes{fichiers?.length ? ` (${fichiers.length})` : ''}</h3>
        <button type="button" className="btn btn-petit" onClick={() => champ.current.click()} disabled={envois.length > 0}>
          <IconeTrombone /> Joindre un fichier
        </button>
        <input ref={champ} type="file" multiple hidden onChange={(e) => { joindre(e.target.files); e.target.value = ''; }} />
      </div>
      {erreur && <Erreur>{erreur}</Erreur>}
      {!fichiers && !erreur && <Chargement />}
      {fichiers && (
        <ul className="pj-liste">
          {fichiers.map((f, i) => (
            <li key={f.id} className="pj-ligne">
              <span className="pj-icone">{genre(f) === 'image' ? <IconeImage /> : <IconeFichier />}</span>
              <button type="button" className="pj-nom" onClick={() => setApercu(i)} title={`Afficher « ${f.nom} »`}>
                <span className="pj-titre">{f.nom}</span>
                <span className="pj-sous" title={horodatage(f.created_at)}>
                  {taille(f.taille)} · {nomComplet(f, 'auteur_') || 'Profil supprimé'} · {ilYA(f.created_at)}
                </span>
              </button>
              <button type="button" className="bouton-icone" onClick={() => telecharger(f)} title="Télécharger" aria-label={`Télécharger « ${f.nom} »`}>
                <IconeTelecharger />
              </button>
              {f.peut_retirer && (
                <button type="button" className="mur-effacer pj-retirer" onClick={() => retirer(f)} aria-label={`Retirer « ${f.nom} »`}>Retirer</button>
              )}
            </li>
          ))}
          {envois.map((x, i) => (
            <li key={`envoi-${i}`} className="pj-ligne pj-envoi">
              <span className="roue" aria-hidden="true" />
              <span className="pj-titre">{x.nom}</span>
              <span className="faible">{x.pc} %</span>
            </li>
          ))}
          {fichiers.length === 0 && envois.length === 0 && (
            <li className="pj-vide">Aucun fichier. Glissez-en un ici ou utilisez « Joindre un fichier ».</li>
          )}
        </ul>
      )}
      {apercu !== null && fichiers?.[apercu] && (
        <ApercuFichier
          tacheId={tacheId}
          fichiers={fichiers}
          index={apercu}
          setIndex={setApercu}
          onFermer={() => setApercu(null)}
          onTelecharger={telecharger}
        />
      )}
    </div>
  );
}

/** Aperçu d'une pièce jointe ; ← et → passent à la précédente / suivante. */
function ApercuFichier({ tacheId, fichiers, index, setIndex, onFermer, onTelecharger }) {
  const f = fichiers[index];
  const g = genre(f);
  const [contenu, setContenu] = useState(null); // { url } ou { texte, tronque }
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    if (!g) return undefined;
    let url = null;
    let annule = false;
    setContenu(null); setErreur(null);
    taches.contenuFichier(tacheId, f)
      .then(async (blob) => {
        if (annule) return;
        if (g === 'texte') {
          const texte = await blob.slice(0, TEXTE_MAX).text();
          if (!annule) setContenu({ texte, tronque: blob.size > TEXTE_MAX });
        } else {
          url = URL.createObjectURL(blob);
          setContenu({ url });
        }
      })
      .catch((e) => !annule && setErreur(messageErreur(e)));
    return () => { annule = true; if (url) URL.revokeObjectURL(url); };
  }, [tacheId, f, g]);

  const n = fichiers.length;
  useEffect(() => {
    if (n < 2) return undefined;
    const f2 = (e) => {
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + n) % n);
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % n);
    };
    window.addEventListener('keydown', f2);
    return () => window.removeEventListener('keydown', f2);
  }, [n, setIndex]);

  let corps;
  if (!g) {
    corps = (
      <div className="apercu-indispo">
        <IconeFichier />
        <p>Pas d’aperçu pour ce type de fichier.</p>
        <button type="button" className="btn btn-primaire" onClick={() => onTelecharger(f)}><IconeTelecharger /> Télécharger</button>
      </div>
    );
  } else if (erreur) corps = <Erreur>{erreur}</Erreur>;
  else if (!contenu) corps = <Chargement />;
  else if (g === 'image') corps = <img className="apercu-image" src={contenu.url} alt={f.nom} />;
  else if (g === 'pdf') corps = <iframe className="apercu-pdf" src={contenu.url} title={f.nom} />;
  // eslint-disable-next-line jsx-a11y/media-has-caption
  else if (g === 'video') corps = <video className="apercu-image" src={contenu.url} controls />;
  else if (g === 'audio') corps = <audio src={contenu.url} controls style={{ width: '100%' }} />;
  else {
    corps = (
      <>
        <pre className="apercu-texte">{contenu.texte}</pre>
        {contenu.tronque && <p className="faible" style={{ fontSize: 12 }}>Aperçu limité au premier mégaoctet : téléchargez le fichier pour le lire en entier.</p>}
      </>
    );
  }

  return (
    <Modale
      titre={f.nom}
      onFermer={onFermer}
      className="modale-apercu"
      actions={(
        <>
          {n > 1 && (
            <div className="apercu-nav">
              <button type="button" className="btn btn-petit" onClick={() => setIndex((index - 1 + n) % n)} aria-label="Fichier précédent">‹</button>
              <span className="faible">{index + 1} / {n}</span>
              <button type="button" className="btn btn-petit" onClick={() => setIndex((index + 1) % n)} aria-label="Fichier suivant">›</button>
            </div>
          )}
          <span className="faible apercu-infos">{taille(f.taille)} · {nomComplet(f, 'auteur_') || 'Profil supprimé'}, {horodatage(f.created_at)}</span>
          <button type="button" className="btn" onClick={() => onTelecharger(f)}><IconeTelecharger /> Télécharger</button>
        </>
      )}
    >
      <div className="apercu">{corps}</div>
    </Modale>
  );
}
